'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import type { SxProps, Theme } from '@mui/system';
import {
  ConversationListRoot,
  markChatLayoutPane,
  ConversationListItemAvatar,
  ConversationListItemContent,
  ConversationListTitle,
  ConversationListPreview,
  ConversationListTimestamp,
  ConversationListUnreadBadge,
  ConversationListItemActions,
} from '@mui/x-chat-headless';
import type {
  ConversationListRootProps,
  ConversationListRootSlots,
  ConversationListRootSlotProps,
  ConversationListItemProps,
  ConversationListItemAvatarProps,
  ConversationListItemContentProps,
  ConversationListTitleProps,
  ConversationListPreviewProps,
  ConversationListTimestampProps,
  ConversationListUnreadBadgeProps,
  ConversationListItemActionsProps,
  ConversationListItemOwnerState,
  ConversationListVariant,
} from '@mui/x-chat-headless';
import resolveComponentProps from '@mui/utils/resolveComponentProps';
import { styled, createUseThemeProps } from '../internals/zero-styled';
import { useChatConversationListUtilityClasses } from './chatConversationListClasses';
import type { ChatConversationListClasses } from './chatConversationListClasses';
import { mergeSlotProps } from '../internals/mergeSlotProps';

const useThemeProps = createUseThemeProps('MuiChatConversationList');

export interface ChatConversationListSlots extends ConversationListRootSlots {}

export interface ChatConversationListProps extends ConversationListRootProps {
  className?: string;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<ChatConversationListClasses>;
  /**
   * The visual variant of the conversation list.
   * - `'default'` – shows avatar, title, preview, timestamp, and unread badge.
   * - `'compact'` – shows only a small unread indicator, the title, and an actions button.
   * @default 'default'
   */
  variant?: ConversationListVariant;
}

const ChatConversationListStyled = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root,
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(0.5, 0),
  listStyle: 'none',
  margin: 0,
  width: '100%',
  boxSizing: 'border-box',
}));

const ChatConversationListScrollerStyled = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'Scroller',
  overridesResolver: (_, styles) => styles.scroller,
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid',
  borderRightColor: (theme.vars || theme).palette.divider,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  flexShrink: 0,
  // Conversation-list visibility is driven by `ChatBox`'s JS layout: in narrow
  // (auto) modes it renders a drawer instead of the inline sidebar, and the
  // headless `ConversationListRoot` pins an inline `display: flex` on this
  // scroller. A width-based `@container { display: none }` here could never win
  // against that inline style and would only bite forced `layoutMode="standard"`
  // (where the sidebar must stay visible), so the scroller carries no such hide.
}));

// Plain-div viewport: the scroller above is a regular div (not ScrollArea.Root),
// so we must also replace the viewport slot to avoid rendering ScrollArea.Viewport
// without a ScrollArea.Root context (which throws during SSR).
const ChatConversationListViewportStyled = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'Viewport',
  overridesResolver: (_, styles) => styles.viewport,
})(() => ({
  flex: 1,
  overflow: 'auto',
  overscrollBehavior: 'contain',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
}));

// No-op scrollbar: native overflow on the viewport above handles scrolling,
// so we don't need the Base UI ScrollArea.Scrollbar / Thumb components.
const NoopScrollbar = React.forwardRef<HTMLDivElement>(function NoopScrollbar() {
  return null;
});

// Props forwarded by ConversationListRenderedItem as additionalProps to every item slot.
// They are intentionally NOT forwarded to the DOM to avoid React unknown-prop warnings.
// NOTE: providing a custom shouldForwardProp REPLACES MUI's default exclusion list, so we
// must also explicitly exclude 'ownerState', 'theme', 'sx', and 'as' here.

const itemSlotShouldForwardProp = (prop: string) =>
  ![
    'conversation',
    'selected',
    'unread',
    'focused',
    'variant',
    'ownerState',
    'theme',
    'sx',
    'as',
  ].includes(prop);

const ChatConversationListItemStyled = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'Item',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.item,
})<{
  ownerState?: {
    selected?: boolean;
    unread?: boolean;
    focused?: boolean;
    variant?: ConversationListVariant;
  };
}>(({ theme, ownerState }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
  outline: 'none',
  borderRadius: 0,
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
  backgroundColor: ownerState?.selected
    ? (theme.vars || theme).palette.action.selected
    : 'transparent',
  '&:hover': {
    backgroundColor: ownerState?.selected
      ? (theme.vars || theme).palette.action.selected
      : (theme.vars || theme).palette.action.hover,
  },
  '&:focus-visible': {
    outline: `2px solid ${(theme.vars || theme).palette.primary.main}`,
    outlineOffset: -2,
  },
  ...(ownerState?.variant === 'compact' && {
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: theme.shape.borderRadius,
    '&:hover .MuiChatConversationList-itemActions': {
      opacity: 1,
    },
    '&:focus-within .MuiChatConversationList-itemActions': {
      opacity: 1,
    },
  }),
}));

// ---------------------------------------------------------------------------
// Styled inner roots — pure styling, no rendering logic.
// These are used as the `root` slot inside the unstyled sub-components below.
// ---------------------------------------------------------------------------

const ChatConversationListItemAvatarRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemAvatar',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemAvatar,
})(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: (theme.vars || theme).palette.grey[300],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: (theme.vars || theme).palette.text.secondary,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const ChatConversationListItemContentRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemContent',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemContent,
})(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
}));

const ChatConversationListTitleRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemTitle',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemTitle,
})<{ ownerState?: { unread?: boolean } }>(({ theme, ownerState }) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: ownerState?.unread
    ? theme.typography.fontWeightBold
    : theme.typography.fontWeightMedium,
  color: (theme.vars || theme).palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
}));

const ChatConversationListPreviewRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemPreview',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemPreview,
})(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
}));

const ChatConversationListTimestampRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemTimestamp',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemTimestamp,
})(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.disabled,
  flexShrink: 0,
  alignSelf: 'flex-start',
  paddingTop: '2px',
  whiteSpace: 'nowrap',
}));

const ChatConversationListUnreadBadgeRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemUnreadBadge',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemUnreadBadge,
})<{ ownerState?: { variant?: ConversationListVariant } }>(({ theme, ownerState }) => ({
  minWidth: 18,
  height: 18,
  borderRadius: 9,
  backgroundColor: (theme.vars || theme).palette.primary.main,
  color: (theme.vars || theme).palette.primary.contrastText,
  fontSize: '0.65rem',
  fontWeight: theme.typography.fontWeightBold,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 0.5),
  flexShrink: 0,
  alignSelf: 'center',
  lineHeight: 1,
  ...(ownerState?.variant === 'compact' && {
    minWidth: 8,
    width: 8,
    height: 8,
    borderRadius: '50%',
    padding: 0,
    fontSize: 0,
    overflow: 'hidden',
  }),
}));

// ---------------------------------------------------------------------------
// Item actions — only rendered in the compact variant, revealed on hover/focus.
// ---------------------------------------------------------------------------

const ChatConversationListItemActionsRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemActions',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemActions,
})<{ ownerState?: { variant?: ConversationListVariant } }>(({ theme, ownerState }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  ...(ownerState?.variant === 'compact' && {
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shortest,
    }),
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
    '&:focus-within': {
      opacity: 1,
    },
  }),
}));

// ---------------------------------------------------------------------------
// Wrapper slot components — keep the unstyled rendering logic, inject the
// styled root so MUI theming is applied.
// ---------------------------------------------------------------------------

// Wrapper around the styled item root that satisfies
// `React.JSXElementConstructor<ConversationListItemProps>`. The styled root
// uses `shouldForwardProp` to filter out the conversation-related props at
// runtime, so we just spread them through.
const ChatConversationListItemSlot = React.forwardRef<HTMLDivElement, ConversationListItemProps>(
  function ChatConversationListItemSlot(props, ref) {
    return <ChatConversationListItemStyled ref={ref} {...props} />;
  },
);

ChatConversationListItemSlot.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * The conversation the item renders.
   */
  conversation: PropTypes.shape({
    avatarUrl: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastMessageAt: PropTypes.string,
    metadata: PropTypes.object,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        avatarUrl: PropTypes.string,
        displayName: PropTypes.string,
        id: PropTypes.string.isRequired,
        isOnline: PropTypes.bool,
        metadata: PropTypes.object,
        role: PropTypes.oneOf(['assistant', 'system', 'user']),
      }),
    ),
    readState: PropTypes.oneOf(['read', 'unread']),
    subtitle: PropTypes.string,
    title: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
  /**
   * If `true`, the item has keyboard focus.
   */
  focused: PropTypes.bool,
  /**
   * If `true`, the conversation is selected.
   */
  selected: PropTypes.bool,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * If `true`, the conversation has unread messages.
   */
  unread: PropTypes.bool,
} as any;

const ChatConversationListItemAvatarStyled = React.forwardRef<
  HTMLDivElement,
  ConversationListItemAvatarProps
>(function ChatConversationListItemAvatarStyled(props, ref) {
  return (
    <ConversationListItemAvatar
      ref={ref}
      {...props}
      slots={{ root: ChatConversationListItemAvatarRoot, ...props.slots }}
    />
  );
});

ChatConversationListItemAvatarStyled.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The conversation the item renders.
   */
  conversation: PropTypes.shape({
    avatarUrl: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastMessageAt: PropTypes.string,
    metadata: PropTypes.object,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        avatarUrl: PropTypes.string,
        displayName: PropTypes.string,
        id: PropTypes.string.isRequired,
        isOnline: PropTypes.bool,
        metadata: PropTypes.object,
        role: PropTypes.oneOf(['assistant', 'system', 'user']),
      }),
    ),
    readState: PropTypes.oneOf(['read', 'unread']),
    subtitle: PropTypes.string,
    title: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
  /**
   * If `true`, the item has keyboard focus.
   */
  focused: PropTypes.bool,
  /**
   * If `true`, the conversation is selected.
   */
  selected: PropTypes.bool,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * If `true`, the conversation has unread messages.
   */
  unread: PropTypes.bool,
} as any;

const ChatConversationListItemContentStyled = React.forwardRef<
  HTMLDivElement,
  ConversationListItemContentProps
>(function ChatConversationListItemContentStyled(props, ref) {
  return (
    <ConversationListItemContent
      ref={ref}
      {...props}
      slots={{ root: ChatConversationListItemContentRoot, ...props.slots }}
    />
  );
});

ChatConversationListItemContentStyled.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The conversation the item renders.
   */
  conversation: PropTypes.shape({
    avatarUrl: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastMessageAt: PropTypes.string,
    metadata: PropTypes.object,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        avatarUrl: PropTypes.string,
        displayName: PropTypes.string,
        id: PropTypes.string.isRequired,
        isOnline: PropTypes.bool,
        metadata: PropTypes.object,
        role: PropTypes.oneOf(['assistant', 'system', 'user']),
      }),
    ),
    readState: PropTypes.oneOf(['read', 'unread']),
    subtitle: PropTypes.string,
    title: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
  /**
   * If `true`, the item has keyboard focus.
   */
  focused: PropTypes.bool,
  /**
   * If `true`, the conversation is selected.
   */
  selected: PropTypes.bool,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * If `true`, the conversation has unread messages.
   */
  unread: PropTypes.bool,
} as any;

const ChatConversationListTitleStyled = React.forwardRef<
  HTMLDivElement,
  ConversationListTitleProps
>(function ChatConversationListTitleStyled(props, ref) {
  return (
    <ConversationListTitle
      ref={ref}
      {...props}
      slots={{ root: ChatConversationListTitleRoot, ...props.slots }}
    />
  );
});

ChatConversationListTitleStyled.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The conversation the item renders.
   */
  conversation: PropTypes.shape({
    avatarUrl: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastMessageAt: PropTypes.string,
    metadata: PropTypes.object,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        avatarUrl: PropTypes.string,
        displayName: PropTypes.string,
        id: PropTypes.string.isRequired,
        isOnline: PropTypes.bool,
        metadata: PropTypes.object,
        role: PropTypes.oneOf(['assistant', 'system', 'user']),
      }),
    ),
    readState: PropTypes.oneOf(['read', 'unread']),
    subtitle: PropTypes.string,
    title: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
  /**
   * If `true`, the item has keyboard focus.
   */
  focused: PropTypes.bool,
  /**
   * If `true`, the conversation is selected.
   */
  selected: PropTypes.bool,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * If `true`, the conversation has unread messages.
   */
  unread: PropTypes.bool,
} as any;

const ChatConversationListPreviewStyled = React.forwardRef<
  HTMLDivElement,
  ConversationListPreviewProps
>(function ChatConversationListPreviewStyled(props, ref) {
  return (
    <ConversationListPreview
      ref={ref}
      {...props}
      slots={{ root: ChatConversationListPreviewRoot, ...props.slots }}
    />
  );
});

ChatConversationListPreviewStyled.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The conversation the item renders.
   */
  conversation: PropTypes.shape({
    avatarUrl: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastMessageAt: PropTypes.string,
    metadata: PropTypes.object,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        avatarUrl: PropTypes.string,
        displayName: PropTypes.string,
        id: PropTypes.string.isRequired,
        isOnline: PropTypes.bool,
        metadata: PropTypes.object,
        role: PropTypes.oneOf(['assistant', 'system', 'user']),
      }),
    ),
    readState: PropTypes.oneOf(['read', 'unread']),
    subtitle: PropTypes.string,
    title: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
  /**
   * If `true`, the item has keyboard focus.
   */
  focused: PropTypes.bool,
  /**
   * If `true`, the conversation is selected.
   */
  selected: PropTypes.bool,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * If `true`, the conversation has unread messages.
   */
  unread: PropTypes.bool,
} as any;

const ChatConversationListTimestampStyled = React.forwardRef<
  HTMLDivElement,
  ConversationListTimestampProps
>(function ChatConversationListTimestampStyled(props, ref) {
  return (
    <ConversationListTimestamp
      ref={ref}
      {...props}
      slots={{ root: ChatConversationListTimestampRoot, ...props.slots }}
    />
  );
});

ChatConversationListTimestampStyled.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The conversation the item renders.
   */
  conversation: PropTypes.shape({
    avatarUrl: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastMessageAt: PropTypes.string,
    metadata: PropTypes.object,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        avatarUrl: PropTypes.string,
        displayName: PropTypes.string,
        id: PropTypes.string.isRequired,
        isOnline: PropTypes.bool,
        metadata: PropTypes.object,
        role: PropTypes.oneOf(['assistant', 'system', 'user']),
      }),
    ),
    readState: PropTypes.oneOf(['read', 'unread']),
    subtitle: PropTypes.string,
    title: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
  /**
   * If `true`, the item has keyboard focus.
   */
  focused: PropTypes.bool,
  /**
   * If `true`, the conversation is selected.
   */
  selected: PropTypes.bool,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * If `true`, the conversation has unread messages.
   */
  unread: PropTypes.bool,
} as any;

const ChatConversationListUnreadBadgeStyled = React.forwardRef<
  HTMLDivElement,
  ConversationListUnreadBadgeProps
>(function ChatConversationListUnreadBadgeStyled(props, ref) {
  return (
    <ConversationListUnreadBadge
      ref={ref}
      {...props}
      slots={{ root: ChatConversationListUnreadBadgeRoot, ...props.slots }}
    />
  );
});

// Default inline SVG for the 3-dot "more" icon (MoreHoriz style).

ChatConversationListUnreadBadgeStyled.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The conversation the item renders.
   */
  conversation: PropTypes.shape({
    avatarUrl: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastMessageAt: PropTypes.string,
    metadata: PropTypes.object,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        avatarUrl: PropTypes.string,
        displayName: PropTypes.string,
        id: PropTypes.string.isRequired,
        isOnline: PropTypes.bool,
        metadata: PropTypes.object,
        role: PropTypes.oneOf(['assistant', 'system', 'user']),
      }),
    ),
    readState: PropTypes.oneOf(['read', 'unread']),
    subtitle: PropTypes.string,
    title: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
  /**
   * If `true`, the item has keyboard focus.
   */
  focused: PropTypes.bool,
  /**
   * If `true`, the conversation is selected.
   */
  selected: PropTypes.bool,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * If `true`, the conversation has unread messages.
   */
  unread: PropTypes.bool,
} as any;

function DefaultMoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="6" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="18" cy="12" r="2" />
    </svg>
  );
}

const ChatConversationListItemActionsStyled = React.forwardRef<
  HTMLDivElement,
  ConversationListItemActionsProps
>(function ChatConversationListItemActionsStyled(props, ref) {
  return (
    <ConversationListItemActions
      ref={ref}
      {...props}
      slots={{ root: ChatConversationListItemActionsRoot, ...props.slots }}
    >
      {props.children ?? <DefaultMoreIcon />}
    </ConversationListItemActions>
  );
});

ChatConversationListItemActionsStyled.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The conversation the item renders.
   */
  conversation: PropTypes.shape({
    avatarUrl: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastMessageAt: PropTypes.string,
    metadata: PropTypes.object,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        avatarUrl: PropTypes.string,
        displayName: PropTypes.string,
        id: PropTypes.string.isRequired,
        isOnline: PropTypes.bool,
        metadata: PropTypes.object,
        role: PropTypes.oneOf(['assistant', 'system', 'user']),
      }),
    ),
    readState: PropTypes.oneOf(['read', 'unread']),
    subtitle: PropTypes.string,
    title: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
  /**
   * If `true`, the item has keyboard focus.
   */
  focused: PropTypes.bool,
  /**
   * If `true`, the conversation is selected.
   */
  selected: PropTypes.bool,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * If `true`, the conversation has unread messages.
   */
  unread: PropTypes.bool,
} as any;

const ChatConversationList = React.forwardRef<HTMLDivElement, ChatConversationListProps>(
  function ChatConversationList(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiChatConversationList' });
    const {
      variant = 'default',
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx,
      ...other
    } = props;
    const classes = useChatConversationListUtilityClasses(classesProp);
    const isCompact = variant === 'compact';

    const resolvedSlots: Partial<ConversationListRootSlots> = {
      ...slots,
      root: slots?.root ?? ChatConversationListStyled,
      scroller: slots?.scroller ?? ChatConversationListScrollerStyled,
      viewport: slots?.viewport ?? ChatConversationListViewportStyled,
      scrollbar: slots?.scrollbar ?? NoopScrollbar,
      scrollbarThumb: slots?.scrollbarThumb ?? NoopScrollbar,
      item: slots?.item ?? ChatConversationListItemSlot,
      itemAvatar: slots?.itemAvatar ?? ChatConversationListItemAvatarStyled,
      itemContent: slots?.itemContent ?? ChatConversationListItemContentStyled,
      title: slots?.title ?? ChatConversationListTitleStyled,
      preview: slots?.preview ?? ChatConversationListPreviewStyled,
      timestamp: slots?.timestamp ?? ChatConversationListTimestampStyled,
      unreadBadge: slots?.unreadBadge ?? ChatConversationListUnreadBadgeStyled,
      itemActions: slots?.itemActions ?? ChatConversationListItemActionsStyled,
    };

    // The headless `root` slot is typed as `SlotComponentProps<'div', ...>`,
    // which intentionally does NOT include `sx`. We funnel `sx` to the styled
    // root via a localized assertion — strictly typing the rest of the slot
    // wiring still catches signature drift.
    const rootSlotProps = mergeSlotProps(
      {
        className: clsx(classes.root, isCompact && classes.compact, className),
        sx,
      },
      slotProps?.root,
    ) as unknown as ConversationListRootSlotProps['root'];

    const resolvedSlotProps: ConversationListRootSlotProps = {
      ...slotProps,
      root: rootSlotProps,
      scroller: mergeSlotProps({ className: classes.scroller }, slotProps?.scroller) as any,
      item: (ownerState: ConversationListItemOwnerState) => {
        // `resolveComponentProps` types its return as `T | undefined`, but `mergeSlotProps`
        // never returns `undefined` here, so `?? {}` only satisfies the type.
        return (
          resolveComponentProps(
            mergeSlotProps(
              {
                className: clsx(
                  classes.item,
                  ownerState.selected && classes.itemSelected,
                  ownerState.unread && classes.itemUnread,
                  ownerState.focused && classes.itemFocused,
                ),
              },
              slotProps?.item,
            ),
            ownerState,
          ) ?? {}
        );
      },
      itemAvatar: mergeSlotProps({ className: classes.itemAvatar }, slotProps?.itemAvatar) as any,
      itemContent: mergeSlotProps(
        { className: classes.itemContent },
        slotProps?.itemContent,
      ) as any,
      title: mergeSlotProps({ className: classes.itemTitle }, slotProps?.title) as any,
      preview: mergeSlotProps({ className: classes.itemPreview }, slotProps?.preview) as any,
      timestamp: mergeSlotProps({ className: classes.itemTimestamp }, slotProps?.timestamp) as any,
      unreadBadge: mergeSlotProps(
        { className: classes.itemUnreadBadge },
        slotProps?.unreadBadge,
      ) as any,
      itemActions: mergeSlotProps(
        { className: classes.itemActions },
        slotProps?.itemActions,
      ) as any,
    };

    return (
      <ConversationListRoot
        ref={ref}
        variant={variant}
        {...other}
        slots={resolvedSlots}
        slotProps={resolvedSlotProps}
      />
    );
  },
);

ChatConversationList.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  className: PropTypes.string,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * The visual variant of the conversation list.
   * - `'default'` – shows avatar, title, preview, timestamp, and unread badge.
   * - `'compact'` – shows only a small unread indicator, the title, and an actions button.
   * @default 'default'
   */
  variant: PropTypes.oneOf(['compact', 'default']),
} as any;

// Mirror the headless `ConversationListRoot` pane marker on the Material wrapper so
// `ChatLayout` assigns it to the conversations pane (the symbol lives on the headless
// primitive, not this wrapper). Without it, a split layout with the list visible and
// no active conversation — a single unmarked child — falls back to the thread pane,
// skipping the `conversationsPane` slot/styles.
// `markChatLayoutPane` mutates the component in place and returns it; the return is discarded here.
void markChatLayoutPane(ChatConversationList, 'conversations');

export { ChatConversationList };
